import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {MatCardModule} from '@angular/material/card';
import {Store} from '@ngrx/store';
import {MatDialog} from '@angular/material/dialog';
import {selectLoading, selectSelectedResearchPaper} from '../../services/research-paper/research-paper.selectors';
import {
  deleteResearchPaper,
  deleteScreeningRun,
  startPaperScreeningOnePaper,
  updateResearchPaper,
  uploadResearchPaperPDF
} from '../../services/research-paper/research-paper.actions';
import {of} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {ResearchPaperDTO, ResearchSource} from '../../dto/research-paper';
import {CommonModule, Location} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatIconModule} from '@angular/material/icon';
import {MatTabsModule} from '@angular/material/tabs';
import {MatFormField} from '@angular/material/form-field';
import {MatOptionModule} from '@angular/material/core';
import {MatSelectModule} from '@angular/material/select';
import {
  ResearchPaperOverrideScreeningComponent
} from '../research-paper-override-screening/research-paper-override-screening.component';
import {ScreeningCriteriasResultsComponent} from '../screening-criterias-results/screening-criterias-results.component';
import {ScreeningProcessDTO} from '../../dto/screening-process';
import {ProjectPermissionService} from '../../../../utils/services/project-permission.service';
import {MatChipsModule} from '@angular/material/chips';
import {MatMenuModule} from '@angular/material/menu';
import {FormsModule} from '@angular/forms';
import {MatInputModule} from '@angular/material/input';
import {MatListModule} from '@angular/material/list';
import {ScreeningSettingsResearchDBDTO} from '../../dto/screening-settings';
import {selectQueriesByProjectId} from '../../services/screening-settings/screening-settings.selectors';
import {MatTooltip} from '@angular/material/tooltip';
import {UploadDialogComponent} from '../../../../utils/components/upload-dialog/upload-dialog.component';
import {InfoCardComponent} from '../../../../utils/components/info-card/info-card.component';
import {SnapshotTableComponent} from '../../../snapshot/components/snapshot-table/snapshot-table.component';
import {SummaryPaperComponent} from '../../../summary/components/summary-paper/summary-paper.component';
import {ConfirmDialogComponent} from '../../../../utils/components/confirm-dialog/confirm-dialog.component';
import {getTotalCounts} from '../../helper/research-paper';
import {SaveBtnComponent} from '../../../../utils/components/save-btn/save-btn.component';
import {NgxExtendedPdfViewerModule} from 'ngx-extended-pdf-viewer';
import {environment} from '../../../../../environments/environment';
import {PaperChatDialogComponent} from '../../../chat/components/paper-chat-dialog/paper-chat-dialog.component';
import {LoadingCardComponent} from '../../../../utils/components/loading-card/loading-card.component';
import {ActivatedRoute, Router} from '@angular/router';
import {BadgeComponent} from '../../../../utils/components/badge/badge.component';
import {HeaderComponent} from '../../../../utils/components/header/header.component';
import {EmptyStateComponent} from '../../../../utils/components/empty-state/empty-state.component';

@Component({
  selector: 'app-research-paper-detail',
  imports: [
    CommonModule,
    MatCardModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatTabsModule,
    MatMenuModule,
    MatFormField,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    ResearchPaperOverrideScreeningComponent,
    ScreeningCriteriasResultsComponent,
    MatChipsModule,
    FormsModule,
    MatListModule,
    MatTooltip,
    InfoCardComponent,
    SnapshotTableComponent,
    SummaryPaperComponent,
    SaveBtnComponent,
    NgxExtendedPdfViewerModule,
    LoadingCardComponent,
    BadgeComponent,
    HeaderComponent,
    EmptyStateComponent
  ],
  templateUrl: './research-paper-detail.component.html',
  styleUrl: './research-paper-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResearchPaperDetailComponent {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly projectPermissionService = inject(ProjectPermissionService);
  private readonly location = inject(Location);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  header = viewChild(HeaderComponent);

  readonly paperId = input<number>();

  private readonly _storePaper = this.store.selectSignal(selectSelectedResearchPaper);
  readonly loading = this.store.selectSignal(selectLoading());

  readonly paper = signal<ResearchPaperDTO | undefined>(undefined);

  readonly selectedTabIndex = signal(0);
  readonly editAbstract = signal(false);
  readonly allowPDFRender = signal(true);
  readonly selectedScreeningProcess = signal<ScreeningProcessDTO | undefined>(undefined);

  private readonly queryParams = toSignal(this.route.queryParams, {initialValue: {} as Record<string, string>});

  readonly queries = toSignal(
    toObservable(this.paper).pipe(
      switchMap(p => (p?.queryIds?.length ?? 0) > 0
        ? this.store.select(selectQueriesByProjectId(p!.projectId, p!.queryIds!))
        : of([] as ScreeningSettingsResearchDBDTO[])
      )
    ),
    {initialValue: [] as ScreeningSettingsResearchDBDTO[]}
  );

  readonly editMode = toSignal(
    toObservable(this.paper).pipe(
      switchMap(p => p
        ? this.projectPermissionService.hasWritePermissionViaStore(p.projectId)
        : of(false)
      )
    ),
    {initialValue: false}
  );

  readonly pdfUrl = computed<string | undefined>(() => {
    const p = this.paper();
    if (!p?.hasDownloadedPdf || !p.pdfUrl) return undefined;
    return p.pdfUrl.startsWith('http') ? p.pdfUrl : environment.apiBaseUrl + p.pdfUrl;
  });

  readonly isManuelCreated = computed(() => this.paper()?.source === ResearchSource.SELF_CREATED);


  constructor() {
    effect(() => {
      const p = this._storePaper();
      if (!p) return;
      const isFirstLoad = !untracked(() => this.paper());
      this.paper.set({...p});
      this.selectedScreeningProcess.set(p.latestScreeningRun);
      if (isFirstLoad && !p.abstractText) {
        this.editAbstract.set(true);
      }
    });

    effect(() => {
      const tab = this.queryParams()['tab'];
      if (tab !== undefined) {
        this.selectedTabIndex.set(+tab);
      }
    });
  }

  startScreening(): void {
    const p = this.paper();
    if (!p) return;
    this.store.dispatch(startPaperScreeningOnePaper({projectId: p.projectId, paperId: p.id}));
  }

  deleteScreening(): void {
    const p = this.paper();
    const processId = this.selectedScreeningProcess()?.id;
    if (!p || !processId) return;

    this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      data: {
        title: 'Delete screening results',
        message: this.selectedScreeningProcess()?.criteria,
        warningMessage: 'The screening results will be deleted',
        confirmButtonText: 'Delete',
      },
    }).afterClosed().subscribe(result => {
      if (!result) return;
      this.store.dispatch(deleteScreeningRun({projectId: p.projectId, processId}));
      if (this.header()) {
        this.header()?.onGoBackHandler();
      }
    });
  }

  deletePaper(): void {
    const p = this.paper();
    if (!p) return;
    this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      data: {
        title: 'Delete Paper',
        message: p.title,
        warningMessage: 'This paper will be deleted',
        confirmButtonText: 'Delete',
      },
    }).afterClosed().subscribe(result => {
      if (!result) return;
      this.store.dispatch(deleteResearchPaper({projectId: p.projectId, id: p.id}));
    });
  }

  toggleEditModeAbstract(): void {
    this.editAbstract.update(v => !v);
  }

  update(): void {
    const p = this.paper();
    if (!p) return;
    this.store.dispatch(updateResearchPaper({projectId: p.projectId, id: p.id, paper: p}));
  }

  uploadFile(): void {
    const p = this.paper();
    if (!p) return;
    this.dialog.open(UploadDialogComponent, {width: '400px'})
      .afterClosed()
      .subscribe((result?: File) => {
        if (result) {
          this.store.dispatch(uploadResearchPaperPDF({projectId: p.projectId, id: p.id, file: result}));
        }
      });
  }

  openChatDialog(): void {
    this.allowPDFRender.set(false);
    this.dialog.open(PaperChatDialogComponent, {
      height: '80vh',
      width: '100vw',
      maxWidth: '100vw',
      autoFocus: false,
      position: {bottom: '0'},
      data: {paper: this.paper()},
    }).afterClosed().subscribe(() => this.allowPDFRender.set(true));
  }

  onTabChange(event: any): void {
    const tabIndex = event.index;
    this.selectedTabIndex.set(tabIndex);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {tab: tabIndex},
      queryParamsHandling: 'merge',
    });
  }

  protected readonly getTotalCounts = getTotalCounts;
}
